/* eslint-disable @typescript-eslint/no-explicit-any */

import { AppDispatch, RootState } from '@/store';
import { changeExpanded, update } from '@/store/storeSlice';
import {
  ProductionOrderTree,
  SiteTree,
  TreeItem,
  TSimulationMenuOptions,
  WipItemParams,
  WipTree,
  WipTreeElement,
  WorkPlanTree,
} from '@/store/types';
import {
  formatDateToUnix,
  generateUID,
  parseDate,
  unixTimestampToDate,
} from '@/utils/helpers';
import {
  Button,
  Icon,
  Input,
  Table,
  Tooltip,
  useDebounceCallback,
} from '@data-products-and-ai/react-components';
import {
  TableHeaderType,
  TableRowData,
} from '@data-products-and-ai/react-components/lib/components/DataDisplay/Table/types';
import { TAnchorEl } from '@data-products-and-ai/react-components/lib/components/Layout/Menu/Menu';
import { ReactNode, useContext, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ContextMenu } from '../ContextMenu';
import { sortChildrenByTitleAsc } from '../helpers';
import { SimulationCtx } from '@/contexts/Simulation/SimulationCtx';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Gantt, ViewMode } from 'gantt-task-react';
import MultiEditModal from '@/components/MultiEditModal/MultiEditModal';

type Task = {
  id: string;
  name: string;
  start: Date;
  end: Date;
  type: 'task' | 'milestone' | 'project';
  progress: number;
  dependencies?: string[];
  styles?: {
    progressColor?: string;
    progressSelectedColor?: string;
    backgroundColor?: string;
    backgroundSelectedColor?: string;
    columnWidth?: number;
  };
  hideChildren?: boolean;
  displayOrder?: number;
};

export interface ProductTreeRender extends WipItemParams {
  PO_END_DATE_TIMESTAMP: TreeItem<number>;
  PO_START_DATE_TIMESTAMP: TreeItem<number>;
  PRODUCT_ID: TreeItem;
  PRODUCTION_ORDER: TreeItem;
  PRODUCTION_ORDER_ID: TreeItem;
  WORK_PLAN_ID: TreeItem;
  numChildren: number;
  PO_QUANTITY: number;
  PRODUCT: string;
  WORKPLAN: string;
  START_DATE: string;
  END_DATE: string;
  PTT: number;
}

type ProductTreeRenderGeneric = {
  [K in keyof ProductTreeRender]: string | number | ReactNode;
};
type TProductStructure = {
  id: string;
  data: ProductTreeRenderGeneric;
  children: TProductStructure[];
  isClickable?: boolean;
  isExpanded?: boolean;
  isHidden?: boolean;
  isDisabled?: boolean;
  isSelectable?: boolean;
  area: keyof WipTree;
};

/**
 * Calculates the number of days between two timestamps
 *
 * @param {number} timestamp1 - The first timestamp
 * @param {number} timestamp2 - The second timestamp
 * @returns {number} - The number of days between the two timestamps
 */
function calculateDaysBetweenTimestamps(
  timestamp1: number,
  timestamp2: number,
): number {
  const millisecondsPerDay = 24 * 60 * 60; // Number of milliseconds in a day
  const differenceInMilliseconds = Math.abs(timestamp2 - timestamp1);
  return Math.ceil(differenceInMilliseconds / millisecondsPerDay);
}

/**
 * ProductionOrders Component
 * Renders a table of the simulations production orders with various functionalities such as filtering, expanding rows, and context menu actions
 * Manages the state for selected row, anchor element, and filter query
 */
const ProductionOrders = () => {
  const { setSimulationParams } = useContext(SimulationCtx);
  const [anchorEl, setAnchorEl] = useState<TAnchorEl>(null);
  const [selectedRow, setSelectedRow] = useState<TableRowData | null>(null);
  const [view, setView] = useState<'table' | 'gantt'>('table');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [tasksPerPage] = useState<number>(100);

  const dispatch: AppDispatch = useDispatch();
  const selectedScenario = useSelector((state: RootState) =>
    state.store.Simulation.scenarios.find((item) => item.is_selected),
  );

  const [filterQuery, setFilterQuery] = useState<string>('');

  /**
   * Handles the change of input fields with a debounce effect
   * Updates the filter query state with the new input value
   *
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>} event - The input change event
   */
  const handleInputChangeDebounced = useDebounceCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFilterQuery(event.target.value);
    },
    500,
  );

  const handleDateChange = useDebounceCallback((task: Task) => {
    const start = formatDateToUnix(task.start);
    const end = formatDateToUnix(task.end);
    dispatch(
      update({
        id: task.id,
        area: 'production_orders',
        content: {
          PO_START_DATE_TIMESTAMP: { value: start, originalValue: start },
          PO_END_DATE_TIMESTAMP: { value: end, originalValue: end },
        },
      }),
    );
  }, 500);

  /**
   * Handles the rendering of workplan details
   * Returns a JSX element displaying the workplan steps and operations
   *
   * @param {WorkPlanTree} workplan - The workplan tree object
   */
  const handleWorkplan = (workplan: WorkPlanTree) => {
    const c = workplan.children.map((item: string, idx: number) => {
      const isLastItem = idx === workplan.children.length - 1;
      const borderBottomStyle = isLastItem
        ? {}
        : { borderBottom: 'solid 1px #dadada' };

      return (
        <div
          key={idx}
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            padding: 5,
            ...borderBottomStyle,
          }}
        >
          <div style={{ paddingRight: 10 }}>
            {selectedScenario?.data.operations.byId[item].STEP.value}
          </div>
          <div>
            {selectedScenario?.data.operations.byId[item].OPERATION.value}
          </div>
        </div>
      );
    });

    return <>{c}</>;
  };

  /**
   * Transforms a production order tree item into a product tree render generic object
   *
   * @param {ProductionOrderTree} item - The production order tree item to be transformed
   * @returns {ProductTreeRenderGeneric} - The transformed product tree render generic object
   */
  const TransformProduct = (item: ProductionOrderTree) => {
    let numChildren: number = 0;

    item.children.forEach((child) => {
      if (selectedScenario) {
        const order = selectedScenario.data.production_orders.byId[child];
        if (order && !order.is_disabled) {
          numChildren++;
        }
      }
    });

    const newItem: ProductTreeRenderGeneric = {
      key: item.PRODUCT_ID.originalValue,
      title: item.PRODUCTION_ORDER.value,
      is_disabled: item.is_disabled,
      is_wip: item.is_wip,
      is_deleted: item.is_deleted,
      is_clicked: item.is_clicked,
      is_selectable: item.is_selectable,
      is_selected: item.is_selected,
      is_hidden: item.is_hidden,
      is_match: item.is_match,
      is_open: item.is_open,
      parent: '',
      children: [],
      PO_END_DATE_TIMESTAMP: '',
      PO_START_DATE_TIMESTAMP: '',
      PRODUCT_ID: '',
      PRODUCTION_ORDER: item.PRODUCTION_ORDER.value,
      PRODUCTION_ORDER_ID: item.PRODUCTION_ORDER_ID.value,
      WORK_PLAN_ID: item.WORK_PLAN_ID.value,
      PRODUCT: selectedScenario
        ? selectedScenario.data.products.byId[item.PRODUCT_ID.value].PRODUCT
            .value
        : '',
      PO_QUANTITY: item.PO_QUANTITY.value,
      WORKPLAN: selectedScenario ? (
        <>
          <Tooltip
            key={
              'Tooltip_WP_' +
              selectedScenario.data.work_plans.byId[item.WORK_PLAN_ID.value]
                .WORK_PLAN_ID.value +
              generateUID()
            }
            color="light"
            title={handleWorkplan(
              selectedScenario.data.work_plans.byId[item.WORK_PLAN_ID.value],
            )}
          >
            <div style={{ display: 'inline-block' }}>
              <Button type="headless" allCaps={false}>
                {
                  selectedScenario.data.work_plans.byId[item.WORK_PLAN_ID.value]
                    .WORK_PLAN.value
                }
              </Button>
            </div>
          </Tooltip>
        </>
      ) : (
        ''
      ),
      START_DATE: unixTimestampToDate(
        item.PO_START_DATE_TIMESTAMP.value,
        'dd.mm.yyyy',
      ),
      END_DATE: unixTimestampToDate(
        item.PO_END_DATE_TIMESTAMP.value,
        'dd.mm.yyyy',
      ),
      PTT: calculateDaysBetweenTimestamps(
        item.PO_START_DATE_TIMESTAMP.value,
        item.PO_END_DATE_TIMESTAMP.value,
      ),
      numChildren: numChildren,
    };

    return newItem;
  };

  /**
   * Generates the tree structure for the production orders table
   * SITE > PRODUCTION ORDER TREE
   * Returns an array of product structure objects
   * @param {WipTreeElement<SiteTree>} sitesObject - The sites tree object
   * @param {WipTreeElement<ProductionOrderTree>} originalObject - The production orders tree object
   */
  const generateObjectWithChildren = (
    sitesObject: WipTreeElement<SiteTree>,
    originalObject: WipTreeElement<ProductionOrderTree>,
  ): TProductStructure[] => {
    const objectWithChildren: any[] = [];
    const itemMap: { [itemId: string]: TProductStructure } = {};

    sitesObject.allIds.forEach((itemId: string) => {
      const item = sitesObject.byId[itemId];

      const countSites = originalObject.allIds.reduce((count, id) => {
        if (
          originalObject.byId[id].SITE_ID.value ===
            item.SITE_ID.originalValue &&
          originalObject.byId[id].PARENT_PRODUCTION_ORDER_ID.value === null
        ) {
          return count + 1;
        } else {
          return count;
        }
      }, 0);

      const newItem: TProductStructure = {
        id: item.SITE_ID.originalValue,
        isExpanded: item.is_open,
        isDisabled: item.is_disabled,
        area: 'sites',
        data: {
          key: item.SITE_ID.originalValue,
          title: item.SITE.value,
          is_wip: item.is_wip,
          is_deleted: item.is_deleted,
          is_disabled: item.is_disabled,
          is_clicked: item.is_clicked,
          is_selectable: item.is_selectable,
          is_selected: item.is_selected,
          is_hidden: item.is_hidden,
          is_match: item.is_match,
          is_open: item.is_open,
          parent: '',
          children: [],
          PO_END_DATE_TIMESTAMP: '',
          PO_START_DATE_TIMESTAMP: '',
          PRODUCT_ID: '',
          PRODUCTION_ORDER: item.SITE.value + ' [' + countSites + ']',
          PRODUCTION_ORDER_ID: '',
          WORK_PLAN_ID: '',
          numChildren: '',
          PO_QUANTITY: '',
          PRODUCT: '',
          WORKPLAN: '',
          START_DATE: '',
          PTT: '',
          END_DATE: '',
        },
        children: [],
      };

      objectWithChildren.push(newItem);
      itemMap[itemId] = newItem;
    });

    originalObject.allIds.forEach((itemId: string) => {
      const item = originalObject.byId[itemId];
      const parentId = item.PARENT_PRODUCTION_ORDER_ID.value;
      const siteId = item.SITE_ID.originalValue;
      if (!sitesObject.byId[siteId]) return;

      const newItem: TProductStructure = {
        area: 'production_orders',
        isExpanded: item.is_open,
        isDisabled: item.is_disabled,
        id: item.PRODUCTION_ORDER_ID.value,
        data: TransformProduct(item),
        children: [],
      };

      // If the parent ID is 'root', add the item directly to the object
      if (parentId === null && !item.is_deleted) {
        objectWithChildren
          .find((obj) => obj.id === siteId)
          .children.push(newItem);
        // itemMap[siteId].children.push(newItem);
      }

      // Store the item in the map for later reference
      if (!item.is_deleted) itemMap[itemId] = newItem;
    });

    // Iterate over each item again to assign children to their respective parent nodes
    originalObject.allIds.forEach((itemId: string) => {
      const item = originalObject.byId[itemId];
      const parentId = item.PARENT_PRODUCTION_ORDER_ID;
      const siteId = item.SITE_ID.originalValue;
      if (!sitesObject.byId[siteId]) return;

      // If the item has a parent, add it as a child to the respective parent node
      if (
        parentId !== null &&
        itemMap[parentId.originalValue] &&
        !item.is_deleted
      ) {
        itemMap[parentId.originalValue].children.push(itemMap[itemId]);
      }
    });

    return sortChildrenByTitleAsc(objectWithChildren);
  };

  // Pagination logic for Gantt performance
  const getPaginatedTasks = useCallback(
    (allTasks: Task[], page: number, itemsPerPage: number) => {
      const startIndex = page * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return allTasks.slice(startIndex, endIndex);
    },
    [],
  );

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  if (!selectedScenario) return <>No Scenario</>;

  const objectWithChildren = generateObjectWithChildren(
    selectedScenario.data.sites,
    selectedScenario.data.production_orders,
  );

  const ganttData = flattenToGanttTasks(objectWithChildren);
  const totalPages = Math.ceil(ganttData.length / tasksPerPage);
  const currentTasks = getPaginatedTasks(ganttData, currentPage, tasksPerPage);

  const TableHeaders: TableHeaderType[] = [
    {
      title: 'Production Order',
      align: 'left',
      columnKey: 'PRODUCTION_ORDER',
      columnWidth: '400px',
    },
    { title: 'Product', align: 'center', columnKey: 'PRODUCT' },
    { title: 'Quantity', align: 'center', columnKey: 'PO_QUANTITY' },
    { title: 'Workplan', align: 'center', columnKey: 'WORKPLAN' },

    { title: 'Start Date', align: 'center', columnKey: 'START_DATE' },
    { title: 'End Date', align: 'center', columnKey: 'END_DATE' },
    {
      title: 'Planned Throughput Time',
      align: 'center',
      columnKey: 'PTT',
      columnWidth: '80px',
    },

    {
      title: 'Quantity Assembled',
      align: 'center',
      columnKey: 'numChildren',
      columnWidth: '100px',
    },
  ];

  function flattenToGanttTasks(
    nodes: TProductStructure[],
    parentId: string | null = null,
  ): Task[] {
    let tasks: Task[] = [];

    nodes.forEach((node) => {
      if (node.area === 'production_orders') {
        const data = node.data;
        const id = data.PRODUCTION_ORDER_ID as string;
        const name = data.PRODUCTION_ORDER as string;
        const start = parseDate(data.START_DATE as string, '.') || new Date();
        const end = parseDate(data.END_DATE as string, '.') || new Date();
        const dependencies = parentId ? [parentId] : undefined;

        tasks.push({
          id,
          name,
          start,
          end,
          type: 'task',
          progress: 0, // or your own logic
          dependencies,
          styles: {
            backgroundColor: '#8A00E5',
            progressColor: '#8A00E5',
            backgroundSelectedColor: '#CF9BFF',
            columnWidth: 100,
          },
        });
      } else if (node.children && node.children.length > 0) {
        tasks = tasks.concat(flattenToGanttTasks(node.children));
      }
    });

    return tasks;
  }

  const handleDoubleClick = (value: TableRowData) => {
    const area = value.area as TSimulationMenuOptions;
    if (area !== 'production_orders') return;
    setSimulationParams((prevState) => ({
      ...prevState,
      drawerOpen: true,
      formItem: value.id,
      formArea: area,
    }));
  };

  const handleClick = (value: TableRowData) => {
    /* testDispatch(value) */
    if (!value.children) return;
    if (value.children.length <= 0) return;

    dispatch(
      changeExpanded({
        id: value.id,
        isExpanded: value.isExpanded ?? false,
        area: value.area as keyof WipTree,
      }),
    );
  };

  const handleMenuRight = (
    value: TableRowData,
    event?: React.MouseEvent<HTMLTableRowElement>,
  ) => {
    const area = value.area as keyof WipTree;

    if (area !== 'production_orders') return;
    // event && setAnchorEl(event.currentTarget);
    event &&
      setAnchorEl({
        left: event.clientX,
        top: event.clientY,
      });

    setSelectedRow(value);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          gap: 10,
          marginBottom: 10,
        }}
      >
        <MultiEditModal
          area="production_orders"
          keyProp="PRODUCTION_ORDER_ID"
          name="PRODUCTION_ORDER"
        />
        <ToggleButtonGroup
          size="small"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Tooltip title="Table View" placement="top" arrow disableInteractive>
            <ToggleButton
              value="table"
              onClick={() => {
                setView('table');
              }}
              selected={view === 'table'}
            >
              <Icon icon="IconBrowseAll" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Gantt View" placement="top" arrow disableInteractive>
            <ToggleButton
              value="gantt"
              onClick={() => {
                setView('gantt');
              }}
              selected={view === 'gantt'}
            >
              <Icon icon="IconLineChart" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        {view === 'table' ? (
          <>
            <div style={{ marginBottom: 20 }}>
              <Input
                id="Filter"
                onChange={handleInputChangeDebounced}
                placeholder="Filter"
              />
            </div>
            <ContextMenu
              selectedRow={selectedRow}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              allowDisable={false}
            />
            <Table
              headers={TableHeaders}
              width={'100%'}
              isSelectable={false}
              hasFilter={true}
              filterQuery={filterQuery}
              rows={objectWithChildren}
              pointerCursor={true}
              typographyTag="textsmall"
              onRowClick={(value) => handleClick(value)}
              onRowDoubleClick={(value) => handleDoubleClick(value)}
              onRowRightClick={(value, event) => handleMenuRight(value, event)}
              stickyHeader={true}
              headerVerticalAlign="bottom"
            />
          </>
        ) : (
          <div>
            <Gantt
              tasks={currentTasks}
              viewMode={ViewMode.Month}
              listCellWidth="155px"
              locale="en-GB"
              onDateChange={handleDateChange}
            />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 10,
            padding: '0 10px',
          }}
        >
          <div style={{ display: 'flex', gap: 10 }}>
            <Button
              type="secondary"
              size="small"
              disabled={currentPage === 0}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Previous
            </Button>
            <span style={{ alignSelf: 'center' }}>
              Page {currentPage + 1} of {totalPages}
            </span>
            <Button
              type="secondary"
              size="small"
              disabled={currentPage >= totalPages - 1}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
        <span style={{ textAlign: 'center', display: 'block' }}>
              Showing {currentPage * tasksPerPage + 1} -{' '}
              {Math.min((currentPage + 1) * tasksPerPage, ganttData.length)} of{' '}
              {ganttData.length} Production Orders
        </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductionOrders;
